/**
 * The Response to Reviewers Generator: split a pasted review into numbered comments,
 * let the author choose a stance for each, and assemble a point-by-point response
 * letter.
 *
 * ## Splitting
 *
 * Reviews arrive in every shape — "Reviewer #1:", "Reviewer: 1" (ScholarOne), "Reviewer
 * #2 (Remarks to the Author):", "Referee B", with or without an editor's note on top —
 * so the parser works in two passes:
 *
 * 1. **Groups.** A reviewer or editor heading starts a group; text after the heading on
 *    the same line belongs to it. Text before the first heading is the editor's when
 *    headings follow, or the single reviewer's when none do. Boilerplate lines
 *    ("Comments to the Author", "Recommendation: Major revision") and a decision
 *    letter's own greeting and sign-off are dropped.
 * 2. **Comments.** Inside a group, numbered items ("1.", "2)", "(3)", "Comment 4:",
 *    "Q5") start comments when the reviewer numbered anything; otherwise bullets do;
 *    otherwise each paragraph is one comment. Unnumbered text before the first number —
 *    usually the reviewer's summary — becomes its own comment, and "Major comments" /
 *    "Minor comments" headings are kept as sections rather than treated as comments.
 *
 * Every comment is then editable, mergeable and removable, because no heuristic reads
 * every reviewer correctly — the split is a starting point the author checks.
 */

import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  addressName,
  asFragment,
  asSentence,
  cleanBlock,
  cleanLine,
  compose,
  emphasis,
  normalizeLines,
  orPlaceholder,
  placeholder,
  splitItems,
  stripHonorific,
  strong,
} from "@/lib/tool-kit/correspondence/compose";

export const STANCES = ["agree", "partial", "disagree", "thanks"] as const;

export type Stance = (typeof STANCES)[number];

export const RESPONSE_EDITOR_TITLES = [
  "dr",
  "drNoStop",
  "professor",
  "none",
] as const;

export type ResponseEditorTitle = (typeof RESPONSE_EDITOR_TITLES)[number];

export interface ReviewerComment {
  /** Stable within one split: the React key and the handle for edits. */
  id: string;
  /** "1", "2", "A"… for reviewers; "editor" for the editor. */
  group: string;
  /** The reviewer's own subheading ("Minor comments"), or "". */
  section: string;
  /** The comment as the reviewer wrote it; paragraphs separated by a blank line. */
  text: string;
  stance: Stance;
  page: string;
  lines: string;
  /** What the authors changed — sentences. */
  change: string;
  /** Why the authors kept something — sentences. */
  rationale: string;
  /** The revised passage, quoted in the letter when given. */
  newText: string;
}

export const EDITOR_GROUP = "editor";

const LINE_BREAKS = /\r\n?/g;
const SEPARATOR = "(?:\\s*$|\\s*[:.)\\u2013\\u2014-]\\s*(.*)$)";
const PARENTHETICAL = "(?:\\s*\\([^)]*\\))?";
const REVIEWER_HEADER = new RegExp(
  `^(?:comments?\\s+(?:from|by|of)\\s+)?(?:the\\s+)?(?:reviewer|referee)\\s*(?:#|no\\.?|number)?\\s*:?\\s*(\\d{1,2}|[a-z])\\b${PARENTHETICAL}${SEPARATOR}`,
  "i"
);
const EDITOR_HEADER = new RegExp(
  `^(?:(?:associate|academic|handling|managing|deputy|section|guest|senior)\\s+)?editor(?:['’]s)?(?:[\\s-]+in[\\s-]+chief)?(?:\\s+comments?)?(?:\\s+(?:to|for)\\s+(?:the\\s+)?authors?)?${PARENTHETICAL}${SEPARATOR}`,
  "i"
);
const SKIP_LINE =
  /^(?:(?:comments?|remarks?)\s+(?:to|for)\s+(?:the\s+)?authors?|reviewers?['’]?s?['’]?\s+comments?(?:\s+(?:to|for)\s+(?:the\s+)?authors?)?|recommendation\s*:.*|confidential\s+comments?.*)\s*:?\s*$/i;
/** A decision letter's own greeting and sign-off are not comments. */
const LETTER_FRAME =
  /^(?:dear\s+[^!?]{1,60},|(?:yours\s+)?(?:sincerely|faithfully|truly)|(?:(?:kind|best|warm)\s+)?regards|best wishes|with best wishes)\s*[,.]?$/i;
const SECTION_HEADER =
  /^(?:(?:major|minor|general|specific|additional|other|technical|editorial|small)\s+(?:comments?|issues?|points?|concerns?|revisions?|suggestions?|remarks?|corrections?)|major|minor)\s*:?\s*$/i;
const NUMBERED =
  /^(?:\(?\d{1,3}[.):]|(?:comments?|points?|issues?|questions?|concerns?|remarks?|q|c|r\d+[.-])\s*#?\s*\d{1,3}\s*[.):–—-]?|#\s*\d{1,3}[.):]?)(?:\s+(.*))?$/i;
const BULLET = /^[-•*–—▪◦·]\s+(.*)$/;
const TRAILING_COLON = /\s*:\s*$/;
const PAGE_PREFIX = /^(?:pp?\.?|pages?)\s*/i;

interface RawGroup {
  key: string;
  lines: string[];
}

/** Pass 1: who said what. */
function splitGroups(raw: string): RawGroup[] {
  const groups: RawGroup[] = [];
  const byKey = new Map<string, RawGroup>();
  const before: string[] = [];
  const state: { current: RawGroup | null; sawHeading: boolean } = {
    current: null,
    sawHeading: false,
  };

  const open = (key: string, firstLine: string | undefined) => {
    let group = byKey.get(key);
    if (!group) {
      group = { key, lines: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.lines.push("");
    if (firstLine) {
      group.lines.push(firstLine.trim());
    }
    state.current = group;
    state.sawHeading = true;
  };

  for (const rawLine of raw.replace(LINE_BREAKS, "\n").split("\n")) {
    const line = cleanLine(rawLine);
    if (SKIP_LINE.test(line) || LETTER_FRAME.test(line)) {
      continue;
    }
    const reviewer = REVIEWER_HEADER.exec(line);
    if (reviewer) {
      open(reviewer[1].toUpperCase(), reviewer[2]);
      continue;
    }
    const editor = EDITOR_HEADER.exec(line);
    if (editor) {
      open(EDITOR_GROUP, editor[1]);
      continue;
    }
    (state.current?.lines ?? before).push(line);
  }

  if (before.some((line) => line !== "")) {
    const key = state.sawHeading ? EDITOR_GROUP : "1";
    const existing = byKey.get(key);
    if (existing) {
      existing.lines.unshift(...before, "");
    } else {
      groups.unshift({ key, lines: before });
    }
  }
  return groups;
}

type SplitMode = "numbered" | "bulleted" | "paragraphs";

function modeOf(lines: readonly string[]): SplitMode {
  if (lines.some((line) => NUMBERED.test(line))) {
    return "numbered";
  }
  if (lines.some((line) => BULLET.test(line))) {
    return "bulleted";
  }
  return "paragraphs";
}

function markerText(line: string, mode: SplitMode): string | null {
  if (mode === "numbered") {
    const match = NUMBERED.exec(line);
    return match ? (match[1] ?? "") : null;
  }
  if (mode === "bulleted") {
    const match = BULLET.exec(line);
    return match ? (match[1] ?? "") : null;
  }
  return null;
}

interface Draft {
  section: string;
  paragraphs: string[][];
}

/** Pass 2: a group's lines as comments, each a list of paragraphs of lines. */
function splitComments(lines: readonly string[]): Draft[] {
  const mode = modeOf(lines);
  const drafts: Draft[] = [];
  const state: {
    section: string;
    current: Draft | null;
    breakPending: boolean;
  } = { section: "", current: null, breakPending: false };

  for (const line of lines) {
    if (line === "") {
      if (mode === "paragraphs") {
        state.current = null;
      }
      state.breakPending = true;
      continue;
    }
    if (SECTION_HEADER.test(line)) {
      state.section = line.replace(TRAILING_COLON, "");
      state.current = null;
      continue;
    }
    const marked = markerText(line, mode);
    if (marked !== null || state.current === null) {
      const first = marked ?? line;
      state.current = {
        section: state.section,
        paragraphs: [first === "" ? [] : [first]],
      };
      drafts.push(state.current);
      state.breakPending = false;
      continue;
    }
    const { paragraphs } = state.current;
    if (state.breakPending && (paragraphs.at(-1)?.length ?? 0) > 0) {
      paragraphs.push([]);
    }
    paragraphs.at(-1)?.push(line);
    state.breakPending = false;
  }
  return drafts;
}

function blankResponse(): Pick<
  ReviewerComment,
  "stance" | "page" | "lines" | "change" | "rationale" | "newText"
> {
  return {
    stance: "agree",
    page: "",
    lines: "",
    change: "",
    rationale: "",
    newText: "",
  };
}

/** Split a pasted review into comments, in the order they were written. */
export function parseReviewerComments(raw: string): ReviewerComment[] {
  const comments: ReviewerComment[] = [];
  for (const group of splitGroups(raw)) {
    for (const draft of splitComments(group.lines)) {
      const text = draft.paragraphs
        .map((paragraph) => paragraph.join(" "))
        .filter((paragraph) => paragraph !== "")
        .join("\n\n");
      if (text === "") {
        continue;
      }
      comments.push({
        id: `c${comments.length + 1}`,
        group: group.key,
        section: draft.section,
        text,
        ...blankResponse(),
      });
    }
  }
  return comments;
}

/* ----------------------------------------------------------------- edits */

export function updateComment(
  comments: readonly ReviewerComment[],
  id: string,
  patch: Partial<Omit<ReviewerComment, "id">>
): ReviewerComment[] {
  return comments.map((comment) =>
    comment.id === id ? { ...comment, ...patch } : comment
  );
}

export function removeComment(
  comments: readonly ReviewerComment[],
  id: string
): ReviewerComment[] {
  return comments.filter((comment) => comment.id !== id);
}

/** Can this comment be merged into the one above it (same reviewer)? */
export function canMergeUp(
  comments: readonly ReviewerComment[],
  id: string
): boolean {
  const index = comments.findIndex((comment) => comment.id === id);
  return index > 0 && comments[index - 1].group === comments[index].group;
}

/**
 * Fold a comment the splitter cut too finely back into the one above it. The text is
 * appended as a new paragraph; the response already written for the upper one is kept.
 */
export function mergeWithPrevious(
  comments: readonly ReviewerComment[],
  id: string
): ReviewerComment[] {
  if (!canMergeUp(comments, id)) {
    return [...comments];
  }
  const index = comments.findIndex((comment) => comment.id === id);
  const previous = comments[index - 1];
  const merged = {
    ...previous,
    text: `${previous.text}\n\n${comments[index].text}`,
  };
  return [
    ...comments.slice(0, index - 1),
    merged,
    ...comments.slice(index + 1),
  ];
}

/* ---------------------------------------------------------------- numbers */

export interface CommentNumbering {
  /** Group key → position among the groups, in order of appearance. */
  groups: string[];
  /** Comment id → its number within its group, from 1. */
  index: Map<string, number>;
}

export function numberComments(
  comments: readonly ReviewerComment[]
): CommentNumbering {
  const groups: string[] = [];
  const counts = new Map<string, number>();
  const index = new Map<string, number>();
  for (const comment of comments) {
    if (!counts.has(comment.group)) {
      groups.push(comment.group);
    }
    const next = (counts.get(comment.group) ?? 0) + 1;
    counts.set(comment.group, next);
    index.set(comment.id, next);
  }
  return { groups, index };
}

/* ---------------------------------------------------------------- letter */

export const RESPONSE_LETTER_BANK = {
  editorTitles: {
    dr: "Dr.",
    drNoStop: "Dr",
    professor: "Professor",
  },
  greeting: "Dear {name},",
  greetingNoName: "Dear Editor,",
  reviewerHeading: "Reviewer {n}",
  editorHeading: "Editor",
  reviewerCommentId: "{n}.{k}",
  editorCommentId: "E.{k}",
  commentLabel: "Comment {id}:",
  responseLabel: "Response:",
  newTextLine: "The revised text now reads: “{text}”",
  pageRef: "page {page}",
  lineRef: "line {lines}",
  linesRef: "lines {lines}",
  summaryHeading: "Summary of changes",
  mainChangesIntro: "The main changes to the manuscript are:",
  tallyIntro: {
    team: "one:In total, we responded to {count} comment:|other:In total, we responded to {count} comments:",
    sole: "one:In total, I responded to {count} comment:|other:In total, I responded to {count} comments:",
  },
  tallyLines: {
    agree: "Revised as suggested: {count}",
    partial: "Partly revised: {count}",
    disagree: "Original approach kept, with reasons given: {count}",
    thanks: "General remarks acknowledged: {count}",
  },
  signOff: "Sincerely,",
  onBehalf: "On behalf of all authors",
  placeholders: {
    title: "Manuscript title",
    name: "Your name",
    change: "Describe the change you made",
    rationale: "Explain your reasoning, with evidence or references",
    location: "page X, lines Y–Z",
  },
  opening: [
    "?journal!sole:Thank you for the opportunity to revise our manuscript “{title}”{idPart} for {journal}.",
    "?journal?sole:Thank you for the opportunity to revise my manuscript “{title}”{idPart} for {journal}.",
    "!journal!sole:Thank you for the opportunity to revise our manuscript “{title}”{idPart}.",
    "!journal?sole:Thank you for the opportunity to revise my manuscript “{title}”{idPart}.",
    "!sole:We are grateful to the reviewers for their careful reading and constructive comments, which have helped us to improve the manuscript.",
    "?sole:I am grateful to the reviewers for their careful reading and constructive comments, which have helped me to improve the manuscript.",
    "¶",
    "!sole:Below, we respond to each comment in turn.",
    "?sole:Below, I respond to each comment in turn.",
    "The reviewers' comments are reproduced in full, each followed by a response.",
    "Page and line numbers refer to the revised manuscript.",
    "?tracked:All changes are also marked in the tracked-changes version of the manuscript.",
  ],
  closing: [
    "?journal!sole:We hope the revised manuscript is now suitable for publication in {journal}.",
    "?journal?sole:I hope the revised manuscript is now suitable for publication in {journal}.",
    "!journal!sole:We hope the revised manuscript is now suitable for publication.",
    "!journal?sole:I hope the revised manuscript is now suitable for publication.",
    "Thank you again for your time and for the reviewers' helpful comments.",
  ],
  responses: {
    agreeTeam: [
      "Thank you for this helpful comment. We agree and have revised the manuscript accordingly.",
      "We agree with the reviewer and have made this change.",
      "This is a fair point, and we have revised the manuscript to address it.",
    ],
    agreeSole: [
      "Thank you for this helpful comment. I agree and have revised the manuscript accordingly.",
      "I agree with the reviewer and have made this change.",
      "This is a fair point, and I have revised the manuscript to address it.",
    ],
    partialTeam: [
      "Thank you for this suggestion, which we have taken up in part.",
      "We agree with part of this comment and have revised the manuscript accordingly.",
      "We appreciate this point and have made some of the suggested changes.",
    ],
    partialSole: [
      "Thank you for this suggestion, which I have taken up in part.",
      "I agree with part of this comment and have revised the manuscript accordingly.",
      "I appreciate this point and have made some of the suggested changes.",
    ],
    remainderTeam: [
      "We have not made the rest of the suggested change.",
      "We have kept the rest of the original text.",
      "We have left the remaining part unchanged.",
    ],
    remainderSole: [
      "I have not made the rest of the suggested change.",
      "I have kept the rest of the original text.",
      "I have left the remaining part unchanged.",
    ],
    disagreeTeam: [
      "We thank the reviewer for this comment and have considered it carefully, but respectfully, we have kept our original approach.",
      "We appreciate the reviewer raising this point. After careful consideration, we have decided not to make this change.",
      "We understand the reviewer's concern, but on reflection we believe the original approach is the more appropriate one.",
    ],
    disagreeSole: [
      "I thank the reviewer for this comment and have considered it carefully, but respectfully, I have kept my original approach.",
      "I appreciate the reviewer raising this point. After careful consideration, I have decided not to make this change.",
      "I understand the reviewer's concern, but on reflection I believe the original approach is the more appropriate one.",
    ],
    thanksTeam: [
      "Thank you for these encouraging comments.",
      "We are grateful for the reviewer's positive assessment of the manuscript.",
      "Thank you for this generous summary of our work.",
    ],
    thanksSole: [
      "Thank you for these encouraging comments.",
      "I am grateful for the reviewer's positive assessment of the manuscript.",
      "Thank you for this generous summary of my work.",
    ],
    locationRevised: [
      "The revised text is on {location}.",
      "See {location} of the revised manuscript.",
      "This change appears on {location}.",
    ],
    locationExplainedTeam: [
      "To make our reasoning clear to readers, we have added a brief explanation on {location}.",
      "We have also added a sentence on {location} so that readers can follow this reasoning.",
      "A short explanation now appears on {location}.",
    ],
    locationExplainedSole: [
      "To make my reasoning clear to readers, I have added a brief explanation on {location}.",
      "I have also added a sentence on {location} so that readers can follow this reasoning.",
      "A short explanation now appears on {location}.",
    ],
  },
} as const;

export interface ResponseLetterBank {
  editorTitles: Readonly<Record<Exclude<ResponseEditorTitle, "none">, string>>;
  greeting: string;
  greetingNoName: string;
  reviewerHeading: string;
  editorHeading: string;
  reviewerCommentId: string;
  editorCommentId: string;
  commentLabel: string;
  responseLabel: string;
  newTextLine: string;
  pageRef: string;
  lineRef: string;
  linesRef: string;
  summaryHeading: string;
  mainChangesIntro: string;
  tallyIntro: Readonly<Record<"team" | "sole", string>>;
  tallyLines: Readonly<Record<Stance, string>>;
  signOff: string;
  onBehalf: string;
  placeholders: Readonly<Record<string, string>>;
  opening: readonly string[];
  closing: readonly string[];
  responses: Readonly<Record<string, readonly string[]>>;
}

export interface ResponseLetterInput {
  editorTitle: ResponseEditorTitle;
  editorName: string;
  journal: string;
  title: string;
  manuscriptId: string;
  name: string;
  soleAuthor: boolean;
  tracked: boolean;
  /** The author's own summary, one change per line. */
  mainChanges: string;
  comments: readonly ReviewerComment[];
}

const BULLET_MARK = "• ";

/** "page 7, lines 112–118" — or "" when neither is given. */
export function locationOf(
  comment: Pick<ReviewerComment, "page" | "lines">,
  bank: Pick<ResponseLetterBank, "pageRef" | "lineRef" | "linesRef">
): string {
  const parts: string[] = [];
  const page = cleanLine(comment.page).replace(PAGE_PREFIX, "");
  if (page !== "") {
    parts.push(t(bank.pageRef, { page }));
  }
  const lines = normalizeLines(comment.lines);
  if (lines.text !== "") {
    parts.push(
      t(lines.plural ? bank.linesRef : bank.lineRef, { lines: lines.text })
    );
  }
  return parts.join(", ");
}

/** A comment's number as the letter writes it: "1.3", or "E.2" for the editor. */
export function commentNumber(
  comment: ReviewerComment,
  numbering: CommentNumbering,
  bank: Pick<ResponseLetterBank, "reviewerCommentId" | "editorCommentId">
): string {
  const k = numbering.index.get(comment.id) ?? 0;
  if (comment.group === EDITOR_GROUP) {
    return t(bank.editorCommentId, { k });
  }
  return t(bank.reviewerCommentId, { n: comment.group, k });
}

function pick(list: readonly string[] | undefined, turn: number): string {
  if (!list || list.length === 0) {
    return "";
  }
  return list[turn % list.length] ?? "";
}

/** The response to one comment: the stance's lead sentence, then the specifics. */
export function responseSentences(
  comment: ReviewerComment,
  turn: number,
  soleAuthor: boolean,
  bank: ResponseLetterBank
): string {
  const who = soleAuthor ? "Sole" : "Team";
  const r = bank.responses;
  const location = locationOf(comment, bank);
  const change = asSentence(comment.change);
  const rationale = asSentence(comment.rationale);
  const ph = (key: string) => placeholder(bank.placeholders[key] ?? key);
  const located = (template: string) =>
    t(template, { location: location === "" ? ph("location") : location });

  const parts: string[] = [pick(r[`${comment.stance}${who}`], turn)];
  if (comment.stance === "agree" || comment.stance === "partial") {
    parts.push(orPlaceholder(change, bank.placeholders.change ?? ""));
    parts.push(located(pick(r.locationRevised, turn)));
  }
  if (comment.stance === "partial") {
    parts.push(pick(r[`remainder${who}`], turn));
  }
  if (comment.stance === "partial" || comment.stance === "disagree") {
    parts.push(orPlaceholder(rationale, bank.placeholders.rationale ?? ""));
  }
  if (comment.stance === "disagree" && location !== "") {
    parts.push(located(pick(r[`locationExplained${who}`], turn)));
  }
  if (comment.stance === "thanks" && change !== "") {
    parts.push(change);
  }
  return parts.filter((part) => part !== "").join(" ");
}

function summaryParagraphs(
  input: ResponseLetterInput,
  bank: ResponseLetterBank,
  locale: string
): string[] {
  const changes = splitItems(input.mainChanges);
  const total = input.comments.length;
  if (changes.length === 0 && total === 0) {
    return [];
  }
  const paragraphs = [strong(bank.summaryHeading)];
  if (changes.length > 0) {
    paragraphs.push(
      [
        bank.mainChangesIntro,
        ...changes.map((change) => `${BULLET_MARK}${change}`),
      ].join("\n")
    );
  }
  if (total > 0) {
    const number = new Intl.NumberFormat(locale);
    const tally = [
      tPlural(
        bank.tallyIntro[input.soleAuthor ? "sole" : "team"],
        locale,
        total,
        { count: number.format(total) }
      ),
    ];
    for (const stance of STANCES) {
      const count = input.comments.filter(
        (comment) => comment.stance === stance
      ).length;
      if (count > 0) {
        tally.push(
          `${BULLET_MARK}${t(bank.tallyLines[stance], { count: number.format(count) })}`
        );
      }
    }
    paragraphs.push(tally.join("\n"));
  }
  return paragraphs;
}

function commentParagraphs(
  input: ResponseLetterInput,
  bank: ResponseLetterBank
): string[] {
  const numbering = numberComments(input.comments);
  const paragraphs: string[] = [];
  let group = "";
  let section = "";
  for (const [turn, comment] of input.comments.entries()) {
    if (comment.group !== group) {
      group = comment.group;
      section = "";
      paragraphs.push(
        strong(
          group === EDITOR_GROUP
            ? bank.editorHeading
            : t(bank.reviewerHeading, { n: group })
        )
      );
    }
    if (comment.section !== "" && comment.section !== section) {
      section = comment.section;
      paragraphs.push(strong(section));
    }
    const label = t(bank.commentLabel, {
      id: commentNumber(comment, numbering, bank),
    });
    paragraphs.push(`${strong(label)} ${emphasis(cleanBlock(comment.text))}`);
    paragraphs.push(
      `${strong(bank.responseLabel)} ${responseSentences(comment, turn, input.soleAuthor, bank)}`
    );
    const revised = asFragment(comment.newText);
    if (revised !== "" && comment.stance !== "disagree") {
      paragraphs.push(t(bank.newTextLine, { text: revised }));
    }
  }
  return paragraphs;
}

/** The whole response letter, as marked paragraphs. */
export function buildResponseLetter(
  input: ResponseLetterInput,
  bank: ResponseLetterBank,
  locale: string
): string[] {
  const editor = stripHonorific(input.editorName);
  const title =
    input.editorTitle === "none" ? "" : bank.editorTitles[input.editorTitle];
  const greeting =
    editor === ""
      ? bank.greetingNoName
      : t(bank.greeting, { name: addressName(title, editor) });

  const journal = asFragment(input.journal);
  const manuscriptId = cleanLine(input.manuscriptId);
  const values = {
    title: orPlaceholder(
      asFragment(input.title),
      bank.placeholders.title ?? ""
    ),
    journal: emphasis(journal),
    idPart: manuscriptId === "" ? "" : ` (${manuscriptId})`,
  };
  const flags = new Set<string>();
  if (input.soleAuthor) {
    flags.add("sole");
  }
  if (journal !== "") {
    flags.add("journal");
  }
  if (input.tracked) {
    flags.add("tracked");
  }

  const signature = [
    bank.signOff,
    orPlaceholder(cleanLine(input.name), bank.placeholders.name ?? ""),
  ];
  if (!input.soleAuthor) {
    signature.push(bank.onBehalf);
  }

  return [
    greeting,
    ...compose(bank.opening, values, flags),
    ...summaryParagraphs(input, bank, locale),
    ...commentParagraphs(input, bank),
    ...compose(bank.closing, values, flags),
    signature.join("\n"),
  ];
}
